import { useNavigate } from "react-router-dom";

import { Button } from "./ui/button";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Page, PageBody } from "./PageLayout";
import { Topbar } from "./Topbar";

interface ErrorStateProps {
  title: string;
  description: string;
  actionLabel?: string;
  to?: string;
  /** Visual tone of the surrounding Alert. Defaults to the neutral
   *  "default" variant; pages that previously used a red destructive
   *  alert can opt in via "destructive". */
  variant?: "default" | "destructive";
  /** Optional breadcrumb trail rendered in the Topbar. Omit to fall back
   *  to a plain title-only Topbar. */
  breadcrumb?: { label: string; to?: string }[];
  /** Title for the Topbar when no breadcrumb is given. Falls back to
   *  `title` when omitted. */
  topbarTitle?: string;
  /** Optional content rendered between the Topbar and the body. Used by
   *  project pages to keep the <ProjectTabs /> sub-navigation visible
   *  on error paths. */
  tabs?: React.ReactNode;
}

/**
 * ErrorState is the standard "something is wrong with this view" shell
 * used by the project pages for both the "project not found" path and the
 * "report not found" / "report failed to load" paths. It centralizes the
 * Topbar + narrow PageBody + Alert + back-action button shape so the
 * per-page call sites can stay focused on their own copy.
 */
export function ErrorState({
  title,
  description,
  actionLabel = "Back to projects",
  to = "/",
  variant = "default",
  breadcrumb,
  topbarTitle,
  tabs,
}: ErrorStateProps) {
  const navigate = useNavigate();
  return (
    <Page>
      <Topbar
        title={breadcrumb ? undefined : topbarTitle ?? title}
        breadcrumb={breadcrumb}
      />
      {tabs}
      <PageBody width="narrow">
        <Alert className="rounded-xl" variant={variant}>
          <AlertTitle>{title}</AlertTitle>
          <AlertDescription>{description}</AlertDescription>
          <Button onClick={() => navigate(to)} className="mt-2 self-start">
            {actionLabel}
          </Button>
        </Alert>
      </PageBody>
    </Page>
  );
}

interface ProjectNotFoundProps {
  breadcrumb?: { label: string; to?: string }[];
}

/** ProjectNotFound renders the "no such project" screen used by every
 *  project-scoped page. Pass the breadcrumb so the Topbar reflects the
 *  current section even on the error path. */
export function ProjectNotFound({ breadcrumb }: ProjectNotFoundProps) {
  return (
    <ErrorState
      title="No such project"
      description="The project you requested does not exist or you no longer have access."
      topbarTitle="Project not found"
      breadcrumb={breadcrumb}
    />
  );
}
